import { Box, Typography } from '@mui/material';
import { useMemo, useState } from 'react';

import { Button, MultiEmailInput } from '@/components';
import {
  useAddOrganizationMembersMutation,
  useFetchPlanLimitsQuery,
} from '@/store';
import { SnackbarUtils } from '@/utils';

import type { FormRow } from '../types';
import {
  createRow,
  getAddMemberErrorMessage,
  isFormDirty,
  isFormInvalid,
  isUnderSeatCap,
  mergeFixedFormRows,
  transformCSVToFormData,
  transformFormDataToPayload,
  validateForm,
} from '../utils';
import { CsvUpload } from './CsvUpload';
import { FailedInvites } from './FailedInvites';
import { InviteMembersForm } from './InviteMembersForm';
import { SeatCapProgressIndicator } from './SeatCapProgressIndicator';

const INITIAL_FORM = [createRow()];
type BasicInviteStep = 'collect' | 'duplicate-emails' | 'finalize-emails';
type CsvInviteStep = 'collect' | 'fix-csv' | 'finalize-csv';
type Step = BasicInviteStep | CsvInviteStep;

export const InviteMembers = ({
  onSuccess,
  activeMemberEmails,
}: {
  onSuccess: () => void;
  activeMemberEmails: string[];
}) => {
  const { data: planLimits } = useFetchPlanLimitsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [step, setStep] = useState<Step>('collect');
  const [emails, setEmails] = useState<string[]>([]);
  const [displayNames, setDisplayNames] = useState<Record<string, string>>({});
  const [form, setForm] = useState(INITIAL_FORM);
  const [fixableCsvRows, setFixableCsvRows] = useState<FormRow[]>([]);

  const [addOrganizationMembers, { isLoading: isInviting }] =
    useAddOrganizationMembersMutation();

  const activeMemberEmailsSet = useMemo(
    () => new Set(activeMemberEmails.map(email => email.trim().toLowerCase())),
    [activeMemberEmails],
  );
  const isEmailNew = (email: string) =>
    !activeMemberEmailsSet.has(email.trim().toLowerCase());

  const inviteNewMembers = async (form: FormRow[]) => {
    const validatedForm = validateForm(form);
    if (isFormInvalid(validatedForm)) {
      setForm(validatedForm);
      return;
    }

    const newMembers = transformFormDataToPayload(
      validatedForm,
      activeMemberEmailsSet,
    );

    if (newMembers.length === 0) {
      SnackbarUtils.success('All members are already in the organization');
      onSuccess?.();
      return;
    }

    try {
      await addOrganizationMembers({
        newMembers,
        sendInvite: true,
      }).unwrap();

      const successMessage =
        newMembers.length === 1
          ? '1 new invitation has been sent!'
          : `${newMembers.length} new invitations have been sent!`;

      SnackbarUtils.success(successMessage);
      onSuccess?.();
    } catch (error: any) {
      const errorMessage = getAddMemberErrorMessage(error, newMembers.length);
      SnackbarUtils.error(errorMessage);
    }
  };

  const handleSubmitEmails = () => {
    const formData = emails
      .filter(email => isEmailNew(email))
      .map(email => ({
        ...createRow(),
        inputs: {
          email,
          name: displayNames[email.trim().toLowerCase()] ?? '',
        },
      }));
    setForm(formData);

    if (formData.length < emails.length) {
      setStep('duplicate-emails');
    } else {
      setStep('finalize-emails');
    }
  };

  if (!planLimits) return null; //TODO loading state?

  const handleCsvUploadPreview = (data: { email: string; name: string }[]) => {
    const { formData, fixableRows } = transformCSVToFormData(data);
    setForm(formData);
    setFixableCsvRows(fixableRows);
    setStep(fixableRows.length > 0 ? 'fix-csv' : 'finalize-csv');
  };

  if (step === 'finalize-csv') {
    return (
      <Box sx={styles.root}>
        <Box sx={styles.main}>
          <Box>
            <Typography variant="h2">Preview CSV Import</Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>
              Review the data from your CSV file before adding and inviting.
            </Typography>
          </Box>
          <InviteMembersForm
            form={form}
            setForm={setForm}
            includeWarnings={true}
          />
        </Box>
        <Box sx={styles.footerActions}>
          <Button
            onClick={() => setStep('collect')}
            variant="secondary"
            fullWidth={false}
            skinny={false}
            label="Back"
          />
          <Button
            onClick={() => inviteNewMembers(form)}
            isLoading={isInviting}
            label="Add and Invite"
            fullWidth={false}
            skinny={false}
            disabled={!isFormDirty(form) || isFormInvalid(form)}
          />
        </Box>
      </Box>
    );
  }

  if (step === 'fix-csv') {
    const handleOnContinue = () => {
      const validated = validateForm(fixableCsvRows);
      if (isFormInvalid(validated)) {
        setFixableCsvRows(validated);
        return;
      }

      setFixableCsvRows([]);
      setForm(mergeFixedFormRows(form, validated));
      setStep('finalize-csv');
    };

    return (
      <Box sx={styles.root}>
        <Box sx={styles.main}>
          <Box>
            <Typography variant="h2">Invite Members</Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>
              Resolve the issues below to continue, or upload a new CSV file.
            </Typography>
          </Box>
          <InviteMembersForm
            form={fixableCsvRows}
            setForm={setFixableCsvRows}
            includeRowColumn={true}
          />
        </Box>
        <Box sx={styles.footerActions}>
          <Button
            onClick={() => setStep('collect')}
            variant="secondary"
            fullWidth={false}
            skinny={false}
            label="Back"
          />
          <Button
            label="Continue"
            onClick={handleOnContinue}
            fullWidth={false}
            skinny={false}
            disabled={
              !isFormDirty(fixableCsvRows) || isFormInvalid(fixableCsvRows)
            }
          />
        </Box>
      </Box>
    );
  }

  if (step === 'finalize-emails') {
    return (
      <Box sx={styles.root}>
        <Box sx={styles.main}>
          <Typography variant="h2">Add Member Details</Typography>
          <InviteMembersForm
            editableEmails={false}
            form={form}
            setForm={setForm}
          />
        </Box>
        <Box sx={styles.footerActions}>
          <Button
            onClick={() => setStep('collect')}
            variant="secondary"
            fullWidth={false}
            skinny={false}
            label="Back"
          />
          <Button
            onClick={() => inviteNewMembers(form)}
            isLoading={isInviting}
            label="Add and Invite"
            fullWidth={false}
            skinny={false}
            disabled={!isFormDirty(form) || isFormInvalid(form)}
          />
        </Box>
      </Box>
    );
  }

  if (step === 'duplicate-emails') {
    const duplicateEmails = emails.filter(email => !isEmailNew(email));
    const allFailed = duplicateEmails.length === emails.length;

    return (
      <Box sx={styles.root}>
        <Box sx={styles.main}>
          <Typography variant="h2">
            {duplicateEmails.length === 1
              ? "1 Invite Can't be Sent"
              : `${duplicateEmails.length} Invites Can't be Sent`}
          </Typography>
          <FailedInvites duplicateEmails={duplicateEmails} />
        </Box>
        <Box sx={styles.footerActions}>
          <Button
            onClick={() => setStep('collect')}
            variant="secondary"
            fullWidth={false}
            skinny={false}
            label="Back"
          />
          <Button
            onClick={() => setStep('finalize-emails')}
            variant="primary"
            fullWidth={false}
            skinny={false}
            label="Continue"
            disabled={allFailed}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.root}>
      <Box sx={styles.main}>
        <Typography variant="h2">Invite Members</Typography>
        <Box sx={styles.multiEmailInput}>
          <MultiEmailInput
            label="Invite by email"
            emails={emails}
            setEmails={setEmails}
            onDisplayNames={names =>
              setDisplayNames(prev => ({ ...prev, ...names }))
            }
            inputAdornment={
              <SeatCapProgressIndicator
                cap={planLimits.seatCap?.seatCap}
                count={activeMemberEmails.length + emails.length}
                hasError={
                  !isUnderSeatCap(
                    planLimits,
                    emails.length,
                    planLimits.usedSeats ?? 0,
                  )
                }
                showProgressBar={false}
              />
            }
          />
          <Button
            onClick={handleSubmitEmails}
            label="Invite"
            fullWidth={false}
            skinny={false}
            disabled={
              emails.length === 0 ||
              !isUnderSeatCap(
                planLimits,
                emails.length,
                planLimits.usedSeats ?? 0,
              )
            }
          />
        </Box>
        <CsvUpload
          label="Invite by CSV"
          onPreview={handleCsvUploadPreview}
          planLimits={planLimits}
          activeMemberCount={planLimits.usedSeats ?? 0}
        />
      </Box>
    </Box>
  );
};

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    width: { xs: '100%', md: '800px' },
    minHeight: { xs: '50vh', md: '480px' },
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  multiEmailInput: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 2,
    mb: 3,
  },
  footerActions: {
    display: 'flex',
    alignItems: 'center',
    ml: 'auto',
    gap: 2.5,
    mt: 2,
  },
};
